import React, { useEffect } from "react";
import {
  getSplitTreatmentsInGetInitialProps,
  sendClientSideSplitTrackEvent,
} from "@leafly-com/split-next";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { NextPage } from "next";

import { getDispensary } from "api/requests/getDispensary";
import { getDispensaryDeals } from "api/requests/getDispensaryDeals";
import getGeolocation from "api/requests/getGeolocation";
import { Deal } from "custom-types/Deals";
import { Dispensary } from "custom-types/Dispensary";
import { DISPENSARY_GEOLOCATION_LOAD } from "redux/reducers/dispensary";
import { getDomainCountryCode } from "redux/selectors/config";
import { getPrimaryAddress } from "redux/selectors/dispensary";
import { isInValidationGroup } from "utils/dispensaryUtils";
import {
  defaultMenuType,
  getMenuTypeCookie,
  includesDualMenuTag,
} from "utils/menuTypeUtils";
import { getSplitKey } from "utils/split/getSplitKey";
import { validateDispensary } from "utils/validateDispensary";

import DealsDisclaimer from "components/Dispensary/DealsDisclaimer";
import DealsSection from "components/Dispensary/DealsSection";
import DispensaryHead from "components/Dispensary/DispensaryHead";
import DispensaryHeaderSlim from "components/Dispensary/DispensaryHeaderSlim/DispensaryHeaderSlim";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import PromotionsSection from "components/Dispensary/PromotionsSection";

import DispensaryBreadcrumbs from "../../../components/Dispensary/DispensaryBreadcrumbs";

type DealsPageProps = {
  deals: Deal[];
  dispensary: Dispensary;
  menuData: {
    isDualLicenseDispensary: boolean;
    userMedRecPreference: string;
  };
  shopEveryMenuMVPEnabled?: boolean;
};

const DealsPage: NextPage<DealsPageProps> = ({
  deals,
  dispensary,
  menuData,
  shopEveryMenuMVPEnabled,
}) => {
  const { isDualLicenseDispensary, userMedRecPreference } = menuData;
  const [menuType, setMenuType] = React.useState(
    userMedRecPreference || defaultMenuType
  );

  useEffect(() => {
    sendClientSideSplitTrackEvent(
      "webDispensary_menuView_frontend",
      getSplitKey()
    );
  }, []);

  return (
    <>
      <DispensaryHead dispensary={dispensary} pageType="deals" />
      <DispensaryHeaderSlim
        hideRequestOnlineOrdering={shopEveryMenuMVPEnabled}
      />
      <DispensaryTabs activeTab={DISPENSARY_PAGES.deals} />

      <div data-testid="deals__content" className="container py-xxl">
        <DealsSection
          deals={deals}
          dispensaryId={dispensary.id}
          isDualMenuDispensary={isDualLicenseDispensary}
          menuType={menuType}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
          setMenuType={setMenuType as any}
        />
        <PromotionsSection />
        <DealsDisclaimer />
        <DispensaryBreadcrumbs />
      </div>
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
DealsPage.getInitialProps = async (context): Promise<any> => {
  const { req, res, query, store } = context;
  const { slug } = query;

  const countryCode = getDomainCountryCode(store.getState());

  try {
    const dispensary = await getDispensary(slug as string);

    const { redirect } = await validateDispensary({
      countryCode,
      dispensary,
      query,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      req: req!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      res: res!,
    });

    if (redirect) {
      return;
    }

    if (dispensary) {
      store.dispatch({
        dispensary,
        type: "dispensary/load",
      });

      const userMedRecPreference = getMenuTypeCookie(req);
      const isDualLicenseDispensary = includesDualMenuTag(
        dispensary.tags || []
      );

      const menuData = {
        isDualLicenseDispensary,
        userMedRecPreference,
      };

      const address = getPrimaryAddress(store.getState());

      const [deals, geolocation] = await Promise.all([
        getDispensaryDeals(String(slug)),
        getGeolocation({ address }).catch(() => undefined),
      ]);

      if (geolocation && dispensary?.slug) {
        store.dispatch({
          dispensarySlug: dispensary.slug,
          geolocation,
          type: DISPENSARY_GEOLOCATION_LOAD,
        });
      }

      let shopEveryMenuMVPEnabled;
      if (
        isInValidationGroup(
          dispensary?.id,
          dispensary?.onlineFulfillmentEnabled
        )
      ) {
        const {
          shopEveryMenuMVP_test: { treatment: splitTreatment },
        } = await getSplitTreatmentsInGetInitialProps(
          "shopEveryMenuMVP_test",
          getSplitKey(context)
        );

        shopEveryMenuMVPEnabled = splitTreatment === "on";
      }

      return {
        deals,
        dispensary,
        menuData,
        shopEveryMenuMVPEnabled,
      };
    }
  } catch (error) {
    return Promise.resolve({
      dispensary: { error: error.message, slug: query.slug },
    });
  }
};

export default DealsPage;
